import type { MapIndexEntry } from '@shared/mapDefinitions.js';
import type { CatalogMap } from '@shared/mapLibrary/catalogMap.js';
import { mapIndex } from '@shared/mapLibrary/mapIndex.js';

/** A map the library offers: a built-in one, or one from the catalog. */
export type CatalogEntry = {
  type: string;
  layer: 'base' | 'overlay';
  /** Its own name; a built-in map's is translated instead. */
  name?: string;
  countries?: string[];
  category?: string;
} & (
  | { index: MapIndexEntry; map?: never }
  | { map: CatalogMap; index?: never }
);

/** An entry's technology; the catalog's data leaves out a tile map's. */
export const technologyOf = (entry: CatalogEntry): string =>
  entry.index?.technology ?? entry.map?.technology ?? 'tile';

let catalog: Promise<CatalogEntry[]> | undefined;

/** Loaded on first use, as at thousands of maps it is no part of the start. */
export function loadLibraryCatalog(): Promise<CatalogEntry[]> {
  catalog ??= (async () => {
    const builtIn = mapIndex.map(
      (index): CatalogEntry => ({
        type: index.type,
        layer: index.layer,
        countries: index.countries,
        category: index.category,
        index,
      }),
    );

    const [eli, curatedJson] = await Promise.all([
      // Generated by `scripts/harvest-eli.mjs`; see doc/map-library.md.
      import(
        /* webpackChunkName: "eli-catalog" */
        './eli/eliCatalog.json'
      ),
      // Hand-picked maps ELI lacks, with ids of their own; see doc/map-library.md.
      import(
        /* webpackChunkName: "curated-catalog" */
        './curated/curatedCatalog.json'
      ),
    ]);

    const { maps } = eli.default as unknown as { maps: CatalogMap[] };

    const curated = curatedJson.default.maps as unknown as CatalogMap[];

    return [
      ...builtIn,
      ...[...curated, ...maps].map((map): CatalogEntry => ({ ...map, map })),
    ];
  })().catch((err: unknown) => {
    // A failed chunk load is tried again on the next call, not kept.
    catalog = undefined;

    throw err;
  });

  return catalog;
}

/** The catalog maps of these ids; an id the catalog lacks is left out. */
export async function loadCatalogMaps(
  types: readonly string[],
): Promise<CatalogMap[]> {
  const wanted = new Set(types);

  return (await loadLibraryCatalog()).flatMap((entry) =>
    entry.map && wanted.has(entry.type) ? [entry.map] : [],
  );
}
